/**
 * Product page behaviour: variant selection from chips, and the mobile sticky
 * add-to-cart bar. Dawn's product-form.js still handles the actual add.
 */

class MidasVariants extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-variants]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch (error) {
      console.error('variant data was not valid JSON', error);
      return;
    }

    this.productRoot = this.closest('.pdp');
    this.form = this.productRoot?.querySelector('product-form form');
    this.handleChange = () => this.onChange();
    this.handlePageShow = () => this.onChange();
    this.handleSubmit = (event) => {
      // Read the checked options again at submission: browsers can restore radios
      // without firing change, leaving the hidden variant ID stale.
      const variant = this.find(this.selection);
      this.updateForm(variant);
      if (!variant || !variant.available) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    this.addEventListener('change', this.handleChange);
    this.form?.addEventListener('submit', this.handleSubmit, true);
    window.addEventListener('pageshow', this.handlePageShow);
    this.onChange();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.form?.removeEventListener('submit', this.handleSubmit, true);
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  get selection() {
    // One selected value per option, in option order
    return [...this.querySelectorAll('fieldset')].map(
      (field) => field.querySelector('.pdp__chip-input:checked')?.value ?? null
    );
  }

  find(selection) {
    if (!selection.length || selection.some((value) => value === null)) return undefined;
    return this.variants.find((variant) =>
      variant.options.length === selection.length &&
      selection.every((value, index) => variant.options[index] === value)
    );
  }

  onChange() {
    const variant = this.find(this.selection);

    this.updateLegends();
    this.updateAvailability();
    this.updateForm(variant);
    this.updatePrice(variant);
    this.updateUrl(variant);

    document.dispatchEvent(new CustomEvent('midas:variant:change', { detail: { variant } }));
  }

  updateLegends() {
    [...this.querySelectorAll('fieldset')].forEach((field) => {
      const value = field.querySelector('.pdp__chip-input:checked')?.value ?? '';
      const label = field.querySelector('[data-option-value]');
      if (label) label.textContent = value;
    });
  }

  /**
   * Cross out values that cannot be reached from the current selection,
   * holding every other option fixed. They stay visible and readable —
   * hiding them makes the range look smaller than it is.
   */
  updateAvailability() {
    const selection = this.selection;

    [...this.querySelectorAll('fieldset')].forEach((field, optionIndex) => {
      field.querySelectorAll('.pdp__chip-input').forEach((input) => {
        const candidate = [...selection];
        candidate[optionIndex] = input.value;

        const match = this.variants.find((variant) =>
          candidate.every((value, i) => value === null || variant.options[i] === value)
        );

        const available = Boolean(match && match.available);
        input.nextElementSibling?.classList.toggle('chip--unavailable', !available);
        input.dataset.available = String(available);
      });
    });
  }

  updateForm(variant) {
    const idInput = this.form?.querySelector('[name="id"]');
    const button = this.productRoot?.querySelector('[data-add-button]');
    const text = this.productRoot?.querySelector('[data-add-text]');
    if (!idInput || !button || !text) return;

    if (!variant) {
      button.setAttribute('aria-disabled', 'true');
      text.textContent = window.variantStrings?.unavailable ?? 'Unavailable';
      idInput.disabled = true;
      return;
    }

    idInput.value = variant.id;
    idInput.disabled = false;

    if (variant.available) {
      button.removeAttribute('aria-disabled');
      text.textContent = window.variantStrings?.addToCart ?? 'Add to cart';
    } else {
      button.setAttribute('aria-disabled', 'true');
      text.textContent = window.variantStrings?.soldOut ?? 'Sold out';
    }
  }

  updatePrice(variant) {
    const target = this.productRoot?.querySelector('[data-price]');
    if (!target || !variant) return;

    const money = MidasVariants.formatMoney(variant.price);
    target.textContent = money;

    const sticky = document.querySelector('[data-sticky-price]');
    if (sticky) sticky.textContent = money;

    const klarna = document.querySelector('klarna-placement');
    if (klarna) klarna.dataset.purchaseAmount = String(variant.price);
  }

  updateUrl(variant) {
    if (!variant || !this.dataset.url) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
  }

  static formatMoney(cents) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP',
    }).format(cents / 100);
  }
}

customElements.define('midas-variants', MidasVariants);

/**
 * Sticky add-to-cart. Product pages only, and only after the real button has
 * scrolled out of view.
 */
class MidasStickyAtc extends HTMLElement {
  connectedCallback() {
    this.trigger = document.querySelector('[data-add-button]');
    if (!this.trigger || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.hidden = entry.isIntersecting;
      },
      { threshold: 0 }
    );

    this.observer.observe(this.trigger);

    // Mirror the real button's state so it can never offer a sold-out variant
    document.addEventListener('midas:variant:change', () => {
      const disabled = this.trigger.getAttribute('aria-disabled') === 'true';
      const button = this.querySelector('[data-sticky-add]');
      if (!button) return;
      button.toggleAttribute('disabled', disabled);
      button.textContent = this.trigger.querySelector('[data-add-text]')?.textContent ?? button.textContent;
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

customElements.define('midas-sticky-atc', MidasStickyAtc);

/**
 * Pairs with. Shopify's `recommendations` object is only populated when the
 * section is requested from the recommendations endpoint, so the section
 * renders empty and fetches itself when it nears the viewport.
 */
class MidasRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;

    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) throw new Error(`recommendations returned ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('midas-recommendations');

      // No recommendations is a normal outcome on a small catalogue — leave
      // the section empty rather than showing an empty heading.
      if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
    } catch (error) {
      console.error(error);
    }
  }
}

customElements.define('midas-recommendations', MidasRecommendations);
